import { ZfTypography } from '@zafire/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientBalanceAccountDto } from '../../../api/generated/client';
import { useBalanceSheetSort } from '../../../hooks/useBalanceSheetSort';
import type { BalanceSheetTableData, IModalBalanceEntry } from '../../../interfaces/balance-sheet-table';
import { filterBalanceEntriesForCurrentYear } from '../../../utils/utils';
import { AgGridTable } from '../../atoms';

interface IBalanceSheetTableGridProps {
    balanceSheetData?: ClientBalanceAccountDto;
    tableColDefs: ColDef<BalanceSheetTableData>[];
    showAll?: boolean;
    enableCurrentYearFilter?: boolean;
    onRowActionClick: (modalEntry: IModalBalanceEntry) => void;
}

export const BalanceSheetTable: FunctionComponent<IBalanceSheetTableGridProps> = ({
    balanceSheetData,
    tableColDefs,
    showAll,
    enableCurrentYearFilter,
    onRowActionClick,
}) => {
    const { i18n } = useTranslation();

    const { onSortChanged, sortEntriesWithinCategories } = useBalanceSheetSort();

    const tableData = useMemo<BalanceSheetTableData[]>(() => {
        const categories =
            enableCurrentYearFilter && (!showAll || !balanceSheetData)
                ? filterBalanceEntriesForCurrentYear(balanceSheetData?.categories ?? [])
                : (balanceSheetData?.categories ?? []);

        const sortedCategories = sortEntriesWithinCategories(categories);

        return (
            sortedCategories.flatMap<BalanceSheetTableData>((category) => [
                // Add the category title row (uses the same column fields for values on category level)
                {
                    categoryId: category.id,
                    name: category.name[i18n.language],
                    referenceAmount: category.totalAmount,
                    referenceAmountCurrentYear: category.totalAmountCurrentYear,
                    isBankable: category.bankable,
                    isCategoryTitle: true,
                    onRowActionClick,
                } as BalanceSheetTableData,
                // Add the actual entries for this category
                ...category.entries.map((entry) => ({
                    ...entry,
                    categoryId: category.id,
                    isBankable: category.bankable,
                    isCategoryTitle: false,
                    onRowActionClick,
                })),
            ]) ?? []
        );
    }, [enableCurrentYearFilter, showAll, balanceSheetData, sortEntriesWithinCategories, i18n.language, onRowActionClick]);

    return (
        <AgGridTable
            domLayout="autoHeight"
            rowData={tableData}
            columnDefs={tableColDefs}
            onSortChanged={onSortChanged}
            tooltipShowDelay={100}
            defaultColDef={{
                resizable: false,
                cellStyle: ({ colDef, data }) => ({
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: `var(${colDef.field === 'name' && data?.isCategoryTitle ? '--padding-horizontal-sm' : '--padding-horizontal-md'})`,
                }),
                cellRenderer: ({ colDef, data, value, valueFormatted }: ICellRendererParams<BalanceSheetTableData>) => (
                    <ZfTypography variant="body-large" weight={data?.isCategoryTitle && colDef?.field === 'name' ? 'bold' : 'regular'}>
                        {valueFormatted ?? value}
                    </ZfTypography>
                ),
                comparator: () => 0, // disable ag-grids internal sorting
            }}
            getRowStyle={({ data }) => (data?.isCategoryTitle ? { backgroundColor: '#edeff5' } : undefined)}
            headerRowHeight={45}
            tableRowHeight={45}
        />
    );
};
